/**
 * TeamTab - Sortable tab component with team color indicator.
 *
 * A draggable tab that displays team name with a color indicator.
 * Integrates with dnd-kit for drag and drop reordering.
 */

import { SortableHandle } from '@/components/atoms';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@lean-agility/design-system/react';

export interface TeamTabProps {
  /** Unique ID for sorting */
  id: string;
  /** Team display name */
  name: string;
  /** Team color for the indicator */
  color: string;
  /** Whether this tab is currently active */
  isActive: boolean;
  /** Callback when tab is clicked */
  onClick: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * TeamTab - Sortable team tab with color indicator.
 *
 * @example
 * ```tsx
 * <TeamTab
 *   id={team.id}
 *   name={team.name}
 *   color={team.color}
 *   isActive={activeIndex === index}
 *   onClick={() => setActiveIndex(index)}
 * />
 * ```
 */
export function TeamTab({ id, name, color, isActive, onClick, className }: TeamTabProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      role="tab"
      aria-selected={isActive}
      style={{
        ...style,
        ...(isActive ? { borderBottomColor: color } : {}),
      }}
      onClick={onClick}
      className={cn(
        `flex cursor-pointer items-center gap-1.5 rounded-t-lg px-3 py-2 text-sm font-medium transition-all`,
        isActive
          ? `
            -mb-px border-b-[3px] bg-bg-elevated text-text-primary
            dark:bg-bg-elevated dark:text-text-primary
          `
          : `
            text-text-muted
            hover:bg-bg-muted/50 hover:text-text-primary
            dark:text-text-muted
            dark:hover:bg-bg-elevated/30 dark:hover:text-text-primary
          `,
        isDragging && 'shadow-lg',
        className,
      )}
    >
      {/* Drag Handle */}
      <div onClick={(e) => e.stopPropagation()}>
        <SortableHandle listeners={listeners} attributes={attributes} className="size-3 opacity-50" />
      </div>

      {/* Color dot + Team name */}
      <div className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <span className="max-w-25 truncate">{name}</span>
    </div>
  );
}
